/**
 * The sound library index, as the host reads it (`sound-and-music/contract.md` §2).
 *
 * `library.json` is one object, `{v, generated_at, assets: [row…]}`, written by the art
 * lane's tooling and copied to the sound origin beside the audio. The host fetches it once
 * per session and keeps only the rows it is allowed to play: a row without a licence or a
 * recorded human listen is REFUSED, not skipped (R3.2), and its id is reported, because a
 * silently skipped row is a sound a kid asked for that nobody notices is gone.
 */
import { landsAssetBase } from "../world/lands/Art";
import { evalProbeEnabled } from "../eval/probeFlag";

export type SoundKind = "sfx" | "ambient" | "music";

export interface SoundLoop {
  start_s: number;
  end_s: number;
}

/** One `library.md` §3 row, the fields the host reads. Extra fields pass through untouched. */
export interface SoundRow {
  id: string;
  kind: SoundKind;
  title: string;
  /** Relative to the sound origin root: `sound/sfx/sfx.event.jump.1.mp3`. */
  file: string;
  duration_s?: number;
  loop?: SoundLoop | null;
  bytes?: number;
  licence?: string | null;
  reviewed?: { by?: string; on?: string; tone_ok?: boolean } | null;
  attribution?: string | null;
  /** Set when the row is withdrawn: the id that plays in its place, or null for silence. */
  replaced_by?: string | null;
  /** A withdrawn row stays in the index so a project naming it is "withdrawn", not "missing". */
  withdrawn?: boolean;
  /**
   * The hit (U8, contract §10): ms from the file's start to the first onset, and every onset,
   * written by the art lane's ingest for one-shots. Absent on loops and on older indexes;
   * `hits_ms: []` is a take with no hit (a swell), which plays from 0 whatever `align` says.
   */
  onset_ms?: number | null;
  hits_ms?: number[] | null;
}

export interface SoundIndex {
  rows: Map<string, SoundRow>;
  /** Ids refused at load (R3.2 / R3.3), in file order. */
  rejected: string[];
  source: "live" | "fixture";
  /** The origin the files resolve against (`soundFileUrl`). */
  base: string;
}

/** What a frame may name (contract §1). Looser than a row id on purpose: the index decides. */
export const SOUND_ID_RE = /^(sfx|ambient|music)\.[a-z0-9_.]+$/;
/** What a row must be named (contract §2): `<kind>.<facet>.<term>.<n>`. */
export const SOUND_ROW_ID_RE = /^(sfx|ambient|music)\.[a-z0-9_]+\.[a-z0-9_]+\.[0-9]+$/;

const KINDS: ReadonlySet<string> = new Set<SoundKind>(["sfx", "ambient", "music"]);

type Env = Record<string, string | undefined>;

/**
 * The three build variables, referenced DIRECTLY so every bundler can substitute them: Vite
 * inlines `import.meta.env.VITE_*` only where the full expression appears, and the Astro
 * share page (web/) exposes no VITE_* at all, so its `astro.config.mjs` defines these three
 * from `PUBLIC_*` the way it already does for the API base. Reading them off an `env`
 * object would have left the share island with no switch and no sound.
 */
const BUILD_ENV: Env = {
  VITE_PROJECT_SOUND_ENABLED: import.meta.env.VITE_PROJECT_SOUND_ENABLED,
  VITE_SOUND_ASSET_BASE: import.meta.env.VITE_SOUND_ASSET_BASE,
  VITE_LANDS_ASSET_BASE: import.meta.env.VITE_LANDS_ASSET_BASE,
};

/** The Vite dev server (and vitest), never a build: `import.meta.env.DEV`. */
const DEV_SERVER = !!import.meta.env.DEV;

/** The build switch: the host handles `wbm:sound` at all. Default off; on for dev. */
export function projectSoundEnabled(env: Env = BUILD_ENV): boolean {
  return env.VITE_PROJECT_SOUND_ENABLED === "true";
}

const FIXTURE_KEY = "wbm.sound_index";
export const FIXTURE_BASE = "/sound-fixture";

/**
 * `?wbm_sound_index=fixture`, honoured only with the eval probe on, sticky for the tab,
 * `?wbm_sound_index=live` clears it (contract §6). It lets one eval walk use the fixture
 * index without restarting the stack with a different env.
 */
export function soundIndexFixtureEnabled(): boolean {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("wbm_sound_index");
    if (fromUrl === "live") window.sessionStorage.removeItem(FIXTURE_KEY);
    else if (fromUrl === "fixture") window.sessionStorage.setItem(FIXTURE_KEY, "1");
    return evalProbeEnabled() && window.sessionStorage.getItem(FIXTURE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Where `sound/library.json` and the files live: `VITE_SOUND_ASSET_BASE`, else (a build)
 * the Lands art origin (the same distribution), else the page's own origin. A relative
 * value (`/sound-fixture`) is allowed.
 *
 * On the DEV SERVER an unset base is the checked-in fixture, not the Lands origin: a local
 * run sets that origin to prod's CloudFront (`start.sh --lands`), which refuses a localhost
 * page, and the result was a CORS error on every page of every local stack (evals,
 * 2026-10-22). `bootProjectSound` says so once. A build never defaults this way.
 */
export function soundAssetBase(env: Env = BUILD_ENV, dev: boolean = DEV_SERVER): string {
  const own = (env.VITE_SOUND_ASSET_BASE ?? "").trim().replace(/\/+$/, "");
  if (own) return own;
  if (dev) return FIXTURE_BASE;
  return landsAssetBase(env);
}

/** True when the dev server is playing the fixture because no base was set. */
export function soundAssetBaseDefaulted(env: Env = BUILD_ENV, dev: boolean = DEV_SERVER): boolean {
  return dev && (env.VITE_SOUND_ASSET_BASE ?? "").trim() === "";
}

export function soundIndexUrl(base: string): string {
  return `${base}/sound/library.json`;
}

export function soundFileUrl(base: string, row: SoundRow): string {
  return `${base}/${row.file.replace(/^\/+/, "")}`;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/**
 * Keep the rows the host may play; name the ones it refuses. Pure, so the rule is testable
 * without a fetch. Rows with no usable id at all are counted under `"?"`.
 */
export function parseSoundIndex(raw: unknown): { rows: Map<string, SoundRow>; rejected: string[] } {
  const rows = new Map<string, SoundRow>();
  const rejected: string[] = [];
  if (!isRecord(raw) || !Array.isArray(raw.assets)) return { rows, rejected };
  for (const item of raw.assets) {
    if (!isRecord(item)) { rejected.push("?"); continue; }
    const id = typeof item.id === "string" ? item.id : "?";
    const licence = typeof item.licence === "string" && item.licence.trim() !== "";
    const reviewed = isRecord(item.reviewed);
    const attributed = item.attribution != null;
    // The kind must also be the id's first segment, as the backend loader requires: the
    // host serves a superset of what `find_sounds` can hand Ember, never a subset.
    const kindOk = typeof item.kind === "string" && KINDS.has(item.kind) && id.startsWith(item.kind + ".");
    const fileOk = typeof item.file === "string" && item.file !== "";
    if (!SOUND_ROW_ID_RE.test(id) || !kindOk || !fileOk || !licence || !reviewed || attributed) {
      rejected.push(id);
      continue;
    }
    rows.set(id, item as unknown as SoundRow);
  }
  return { rows, rejected };
}

/**
 * Fetch and parse the index for this page. Never throws: a failed fetch is an empty index.
 * `fresh` revalidates past the browser's cache (the host's refetch on an unknown id).
 */
export async function fetchSoundIndex(
  fetchImpl: typeof fetch = (...a) => fetch(...a),
  env: Env = BUILD_ENV,
  { fresh = false }: { fresh?: boolean } = {},
): Promise<SoundIndex> {
  const fixture = soundIndexFixtureEnabled();
  const base = fixture ? FIXTURE_BASE : soundAssetBase(env);
  const source = fixture || base === FIXTURE_BASE ? "fixture" : "live";
  try {
    const res = await fetchImpl(soundIndexUrl(base), { credentials: "omit", ...(fresh ? { cache: "no-cache" as const } : {}) });
    if (!res.ok) return { rows: new Map(), rejected: [], source, base };
    const parsed = parseSoundIndex(await res.json());
    return { ...parsed, source, base };
  } catch {
    return { rows: new Map(), rejected: [], source, base };
  }
}
